import { memo, useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Eye, Layers, MoveDown } from 'lucide-react'
import { formatOverlapAmount, type GridOverlap } from '../../lib/grid-overlaps.js'
import { formatTime } from '../../lib/utils.js'

interface OverlapBannerProps {
  overlaps:  GridOverlap[]
  /** Recale ce chevauchement seul ; les autres ne bougent pas. */
  onShift:   (overlap: GridOverlap) => Promise<void>
  /** Recale tous les chevauchements de la journée affichée. */
  onShiftAll: () => Promise<void>
  onShow:    (overlap: GridOverlap) => void
  /** Sans droit de déplacement, la barre informe mais ne propose pas de recaler. */
  canShift:  boolean
}

const KIND_HINT: Record<GridOverlap['kind'], string | null> = {
  SHIFTABLE: null,
  DUPLICATE: 'posé deux fois — retirez l’exemplaire en trop',
  FROZEN:    'déjà chargé ou à l’antenne — non recalable',
}

/**
 * Barre des chevauchements, jumelle de la barre des trous et placée au-dessus
 * d'elle : un trou fait passer le bouche-trou, un chevauchement coupe un
 * programme. Elle se calcule depuis la grille chargée, donc elle survit au
 * rechargement et disparaît d'elle-même quand le dernier chevauchement est
 * réglé — plus de notification à fermer à la main.
 */
export const OverlapBanner = memo(function OverlapBanner({
  overlaps, onShift, onShiftAll, onShow, canShift,
}: OverlapBannerProps) {
  const [index, setIndex] = useState(0)
  const [busy, setBusy] = useState<'one' | 'all' | null>(null)
  const count = overlaps.length
  const current = overlaps[Math.min(index, count - 1)]

  // La liste se raccourcit à chaque recalage : on reste sur une position valide.
  useEffect(() => { if (index > 0 && index >= count) setIndex(Math.max(0, count - 1)) }, [count, index])

  const previous = useCallback(() => setIndex(i => (i - 1 + count) % count), [count])
  const next = useCallback(() => setIndex(i => (i + 1) % count), [count])

  const handleShow = useCallback(() => { if (current) onShow(current) }, [current, onShow])
  const handleShift = useCallback(async () => {
    if (!current || busy) return
    setBusy('one')
    try { await onShift(current) } finally { setBusy(null) }
  }, [busy, current, onShift])
  const handleShiftAll = useCallback(async () => {
    if (busy) return
    setBusy('all')
    try { await onShiftAll() } finally { setBusy(null) }
  }, [busy, onShiftAll])

  if (!current) return null

  const shiftableCount = overlaps.filter(overlap => overlap.kind === 'SHIFTABLE').length
  const hint = KIND_HINT[current.kind]

  return (
    <div className="agenda-gap-banner agenda-overlap-banner" role="alert">
      <Layers size={14} aria-hidden="true" className="agenda-gap-banner__icon" />

      <span className="agenda-gap-banner__text">
        <strong>{count === 1 ? '1 chevauchement' : `${count} chevauchements`}</strong>
        {' — '}
        {formatTime(current.startTime).slice(0, 5)}
        {' « '}{current.overItemTitle}{' » déborde de '}
        <strong>{formatOverlapAmount(current.overlapSec)}</strong>
        {' sur « '}{current.itemTitle}{' »'}
      </span>

      {hint && <span className="agenda-gap-banner__next agenda-overlap-banner__hint">{hint}</span>}

      <span className="agenda-gap-banner__actions">
        {count > 1 && (
          <span className="agenda-overlap-banner__nav" aria-label="Chevauchement affiché">
            <button type="button" className="tp-button" onClick={previous} aria-label="Chevauchement précédent">
              <ChevronLeft size={13} aria-hidden="true" />
            </button>
            <span className="agenda-overlap-banner__position">{Math.min(index, count - 1) + 1}/{count}</span>
            <button type="button" className="tp-button" onClick={next} aria-label="Chevauchement suivant">
              <ChevronRight size={13} aria-hidden="true" />
            </button>
          </span>
        )}
        {canShift && shiftableCount > 1 && (
          <button
            type="button"
            className="tp-button agenda-overlap-banner__primary"
            onClick={handleShiftAll}
            disabled={busy !== null}
            title="Décaler, pour chaque chevauchement de la journée, l’élément recouvert à la fin de celui qui déborde"
          >
            <MoveDown size={13} aria-hidden="true" />
            {busy === 'all' ? 'Recalage…' : 'Tout recaler'}
          </button>
        )}
        {canShift && current.kind === 'SHIFTABLE' && (
          <button
            type="button"
            className={`tp-button${shiftableCount > 1 ? '' : ' agenda-overlap-banner__primary'}`}
            onClick={handleShift}
            disabled={busy !== null}
            title={`Décaler « ${current.itemTitle} » à la fin de « ${current.overItemTitle} » ; les autres chevauchements ne bougent pas`}
          >
            <MoveDown size={13} aria-hidden="true" />
            {busy === 'one' ? 'Recalage…' : 'Recaler'}
          </button>
        )}
        <button type="button" className="tp-button" onClick={handleShow} title="Faire défiler la timeline jusqu’au chevauchement">
          <Eye size={13} aria-hidden="true" />
          Voir
        </button>
      </span>
    </div>
  )
})
